import { useEffect, useState } from "react";
import api, { BACKEND_URL, formatApiError } from "../../lib/api";
import { uploadFile } from "../../lib/upload";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Palette, Upload, Save, X, Loader2, Image as ImageIcon } from "lucide-react";

const DEFAULT_KIT = {
  name: "My brand kit",
  primary_color: "#FF2F6E",
  secondary_color: "#8B5CF6",
  accent_color: "#3B82F6",
  logo_url: "",
  font_family: "Space Grotesk",
  tagline: "",
};

const PRESET_PALETTES = [
  { name: "Nexus", primary: "#FF2F6E", secondary: "#8B5CF6", accent: "#3B82F6" },
  { name: "Sunset", primary: "#F97316", secondary: "#EF4444", accent: "#FBBF24" },
  { name: "Emerald", primary: "#10B981", secondary: "#059669", accent: "#84CC16" },
  { name: "Ocean", primary: "#0EA5E9", secondary: "#0284C7", accent: "#8B5CF6" },
  { name: "Mono", primary: "#FFFFFF", secondary: "#A1A1AA", accent: "#71717A" },
  { name: "Cyber", primary: "#EC4899", secondary: "#8B5CF6", accent: "#22D3EE" },
];

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function BrandKitEditor({ kit, onSaved, onCancel }) {
  const isNew = !kit;
  const [form, setForm] = useState(kit ? { ...kit } : { ...DEFAULT_KIT });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    setForm(kit ? { ...kit } : { ...DEFAULT_KIT });
  }, [kit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, primary_color: p.primary, secondary_color: p.secondary, accent_color: p.accent }));
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const r = await uploadFile(file, { label: "logo" });
      set("logo_url", r?.url || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploadingLogo(false); }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Give your kit a name");
      return;
    }
    setSaving(true);
    try {
      await api.post("/studio/brand-kits", form);
      toast.success(isNew ? "Brand kit created" : "Brand kit saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-3xl p-6 md:p-8 space-y-6" data-testid="brand-kit-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-nexus-pink/15 flex items-center justify-center">
            <Palette className="h-5 w-5 text-nexus-pink" />
          </div>
          <div>
            <div className="font-display font-bold text-2xl tracking-tight">
              {isNew ? "New brand kit" : "Edit brand kit"}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-0.5">
              Colours · logo · fonts · tagline
            </div>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="h-9 w-9 rounded-full glass flex items-center justify-center hover:bg-white/5"
          data-testid="brand-kit-cancel-btn"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-5">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Kit name</label>
            <Input
              value={form.name}
              onChange={(e) => set("name", e.target.value.slice(0, 60))}
              placeholder="Summer collection"
              className="bg-white/[0.02] border-white/10 text-white"
              data-testid="brand-kit-name-input"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Presets</label>
            <div className="grid grid-cols-3 gap-2">
              {PRESET_PALETTES.map((p) => (
                <button
                  key={p.name}
                  onClick={() => applyPreset(p)}
                  className="rounded-lg p-2 border border-white/10 hover:border-white/25 transition-colors text-left"
                  data-testid={`brand-kit-preset-${p.name.toLowerCase()}`}
                >
                  <div className="flex gap-1 mb-1.5">
                    {[p.primary, p.secondary, p.accent].map((c, i) => (
                      <div key={i} className="h-5 flex-1 rounded" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">{p.name}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {[
              ["Primary", "primary_color"],
              ["Secondary", "secondary_color"],
              ["Accent", "accent_color"],
            ].map(([label, key]) => (
              <div key={key}>
                <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    className="h-10 w-10 rounded-lg border border-white/10 bg-transparent cursor-pointer"
                    data-testid={`brand-kit-${key}-picker`}
                  />
                  <Input
                    value={form[key]}
                    onChange={(e) => set(key, e.target.value)}
                    className="bg-white/[0.02] border-white/10 text-white flex-1 h-10 text-xs font-mono uppercase"
                    data-testid={`brand-kit-${key}-hex`}
                  />
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Font family</label>
            <Input
              value={form.font_family}
              onChange={(e) => set("font_family", e.target.value.slice(0, 60))}
              placeholder="Space Grotesk"
              className="bg-white/[0.02] border-white/10 text-white"
              data-testid="brand-kit-font-input"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Tagline</label>
            <textarea
              value={form.tagline || ""}
              onChange={(e) => set("tagline", e.target.value.slice(0, 140))}
              rows={2}
              placeholder="A punchy one-liner that appears on every export."
              className="w-full bg-white/[0.02] border border-white/10 rounded-md p-2.5 text-white text-sm resize-none"
              data-testid="brand-kit-tagline-input"
            />
            <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1 text-right">
              {(form.tagline || "").length}/140
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Logo</label>
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 rounded-2xl border border-dashed border-white/10 flex items-center justify-center bg-black overflow-hidden shrink-0">
                {form.logo_url ? (
                  <img src={toAbsolute(form.logo_url)} alt="" className="w-full h-full object-contain" data-testid="brand-kit-logo-preview" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-zinc-600" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center gap-2 h-9 px-3.5 rounded-full border border-white/10 text-white text-xs uppercase tracking-widest font-semibold hover:bg-white/5 cursor-pointer">
                  {uploadingLogo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {uploadingLogo ? "Uploading…" : "Upload logo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                    className="hidden"
                    data-testid="brand-kit-logo-input"
                  />
                </label>
                {form.logo_url && (
                  <button
                    onClick={() => set("logo_url", "")}
                    className="block text-[10px] uppercase tracking-widest text-red-300 hover:text-red-100"
                    data-testid="brand-kit-logo-remove-btn"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-2 border-b border-white/5">Preview</div>
            <div
              className="aspect-video p-6 flex flex-col justify-between"
              style={{
                background: `linear-gradient(135deg, ${form.primary_color} 0%, ${form.secondary_color} 100%)`,
              }}
              data-testid="brand-kit-preview"
            >
              <div className="flex items-center justify-between">
                {form.logo_url ? (
                  <img src={toAbsolute(form.logo_url)} alt="" className="h-10 object-contain" />
                ) : (
                  <div style={{ fontFamily: form.font_family, fontWeight: 800 }} className="text-white text-2xl tracking-tight">{form.name}</div>
                )}
                <div className="h-3 w-3 rounded-full" style={{ background: form.accent_color }} />
              </div>
              <div>
                <div style={{ fontFamily: form.font_family, color: form.accent_color }} className="text-[10px] uppercase tracking-widest mb-1">Tagline</div>
                <div style={{ fontFamily: form.font_family }} className="text-white text-lg font-semibold leading-tight">
                  {form.tagline || "Your one-line story goes here."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button
          onClick={onCancel}
          variant="outline"
          className="rounded-full border-white/10 text-white hover:bg-white/5 h-10 px-5"
          data-testid="brand-kit-cancel-secondary-btn"
        >
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold disabled:opacity-60"
          data-testid="brand-kit-save-btn"
        >
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Save className="h-4 w-4 mr-2" /> Save kit</>}
        </Button>
      </div>
    </div>
  );
}
